'use client'
import { Card, CardContent } from '@/components/ui/card'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { useTranslations } from 'next-intl'
import { getIcon } from '@/lib/dynamic-icons'
import Image from 'next/image'

interface FeatureBlock {
  iconName: string
  translationKey: string
}

interface FeaturesWithQuoteProps {
  featuresNamespace: string
  quoteNamespace: string
  blocks: [FeatureBlock, FeatureBlock] // Exactly 2 blocks
  season?: 'summer' | 'winter'
}

export default function FeaturesWithQuote({ featuresNamespace, quoteNamespace, blocks, season = 'winter' }: FeaturesWithQuoteProps) {
  const tFeatures = useTranslations(featuresNamespace)
  const tQuote = useTranslations(quoteNamespace)
  // Season-aware, people-free texture behind the quote (dirt / snow)
  const isSummer = season === 'summer'
  const quoteBg = isSummer ? '/dust-texture.jpg' : '/snow-texture.jpg'
  // Summer reads warm/orange-dusty; winter stays cool.
  const imgFilter = isSummer
    ? 'sepia(0.55) saturate(1.7) hue-rotate(-12deg) brightness(1.05)'
    : 'brightness(1.14) saturate(1.25) contrast(1.04) hue-rotate(8deg)'
  const veil = isSummer
    ? 'linear-gradient(320deg, rgba(46,22,6,0.95) 0%, rgba(74,38,12,0.84) 55%, rgba(102,56,20,0.68) 100%)'
    : 'linear-gradient(320deg, rgba(11,22,38,0.95) 0%, rgba(30,58,94,0.78) 55%, rgba(120,158,196,0.46) 100%)'

  return (
    <section className="relative mt-14 lg:-mt-16 pb-6 lg:pb-10 z-10">
      <div className="mx-auto max-w-7xl px-6">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {/* Two Feature Blocks */}
          {blocks.map((block, index) => {
            const Icon = getIcon(block.iconName)
            return (
              <Card
                key={index}
                className="relative overflow-hidden bg-neutral-50/95 backdrop-blur-md border-0 shadow-2xl hover:shadow-3xl transition-all duration-300 hover:-translate-y-1"
              >
                <CardContent className="pt-8 pb-8">
                  <div className="relative flex aspect-square size-16 rounded-full bg-[url('/leather.jpg')] bg-cover bg-center border border-[#2a1f10]/50 mb-6 shadow-lg">
                    <Icon className="m-auto size-8 text-black" strokeWidth={1.5} />
                  </div>
                  <h2 className="text-3xl font-semibold mb-3 font-[family-name:var(--font-bebas-neue)] text-neutral-900">
                    {tFeatures(`${block.translationKey}.title`)}
                  </h2>
                  {tFeatures(`${block.translationKey}.description`) && (
                  <p className="text-neutral-700 leading-relaxed">
                    {tFeatures(`${block.translationKey}.description`)}
                  </p>
                  )}
                </CardContent>
              </Card>
            )
          })}

          {/* Will Quote Block */}
          <Card className="relative overflow-hidden border-0 shadow-2xl hover:shadow-3xl transition-all duration-300 hover:-translate-y-1">
            {/* Season resort photo */}
            <Image
              src={quoteBg}
              alt=""
              fill
              aria-hidden
              className="object-cover"
              style={{ filter: imgFilter }}
              sizes="(max-width: 768px) 100vw, 33vw"
            />
            {/* Readability veil */}
            <div className="absolute inset-0" style={{ background: veil }} aria-hidden />
            <CardContent className="pt-8 pb-8 flex flex-col h-full relative z-10">
              {/* Large Quote Mark */}
              <svg
                className="absolute top-4 right-4 size-16 text-white/10"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
              </svg>

              {/* Author at top */}
              <div className="flex items-center gap-3 mb-5">
                <Avatar className="size-14 ring-2 ring-white shadow-lg">
                  <AvatarImage
                    alt={tQuote('name')}
                    src="/willem-vanhoeve-7-laux-la-grange-rental.png"
                    loading="lazy"
                    width="120"
                    height="120"
                  />
                  <AvatarFallback className="text-sm font-medium bg-neutral-700 text-white">
                    WV
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-white">{tQuote('name')}</p>
                  <p className="text-sm text-neutral-300">{tQuote('title')}</p>
                </div>
              </div>

              {/* Quote */}
              <blockquote className="flex-1">
                <p className="text-white leading-relaxed text-lg font-bold">
                  &ldquo;{tQuote('quote')}&rdquo;
                </p>
              </blockquote>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  )
}
